import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Edit, CreditCard, Phone, Mail, MapPin } from 'lucide-react';
import { staffApi } from '../api/mockRepo';
import { Staff } from '../types';
import { getRoleDisplayName, getStatusColor, generateStaffIdCard } from '../utils';
import AccountSummary from '../components/AccountSummary';
import toast from 'react-hot-toast';

const StaffProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [staff, setStaff] = useState<Staff | null>(null);
  const [loading, setLoading] = useState(true);
  const [generatingCard, setGeneratingCard] = useState(false);

  useEffect(() => {
    if (id) {
      loadStaff();
    }
  }, [id]);

  const loadStaff = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const data = await staffApi.getById(id);
      if (!data) {
        toast.error('Staff member not found');
        navigate('/staff');
        return;
      }
      setStaff(data);
    } catch (error) {
      toast.error('Failed to load staff member');
      console.error('Error loading staff:', error);
      navigate('/staff');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateIdCard = async () => {
    if (!staff) return;
    
    try {
      setGeneratingCard(true);
      await generateStaffIdCard(staff);
      await staffApi.updateCardIssued(staff.id);
      toast.success(`ID card generated for ${staff.name}`);
      loadStaff(); // Refresh to show updated cardIssuedAt
    } catch (error) {
      toast.error('Failed to generate ID card');
      console.error('Error generating ID card:', error);
    } finally {
      setGeneratingCard(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!staff) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500">Staff member not found</div>
        <Link
          to="/staff"
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Staff List
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/staff')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{staff.name}</h1>
            <p className="text-gray-600">{staff.empCode} • {getRoleDisplayName(staff.role)}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleGenerateIdCard}
            disabled={generatingCard}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center space-x-2"
          >
            <CreditCard className="w-4 h-4" />
            <span>{generatingCard ? 'Generating...' : 'Generate ID Card'}</span>
          </button>
          <Link
            to={`/staff/${staff.id}/edit`}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <Edit className="w-4 h-4" />
            <span>Edit</span>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Profile */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 h-16 w-16">
                  <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-lg font-medium text-gray-700">
                      {staff.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="text-lg font-medium text-gray-900">{staff.name}</div>
                  <div className="text-sm text-gray-500">{staff.empCode}</div>
                  <div className={`inline-flex px-2 py-1 text-xs rounded-full ${getStatusColor(staff.status)}`}>
                    {staff.status.charAt(0).toUpperCase() + staff.status.slice(1)}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                {staff.phone && (
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-900">{staff.phone}</span>
                  </div>
                )}
                {staff.email && (
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-900">{staff.email}</span>
                  </div>
                )}
                {staff.address && (
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-900">{staff.address}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              {staff.gender && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Gender</div>
                  <div className="text-sm font-medium text-gray-900 capitalize">{staff.gender}</div>
                </div>
              )}
              {staff.dob && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Date of Birth</div>
                  <div className="text-sm font-medium text-gray-900">
                    {new Date(staff.dob).toLocaleDateString()}
                  </div>
                </div>
              )}
              {staff.bloodGroup && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Blood Group</div>
                  <div className="text-sm font-medium text-gray-900">{staff.bloodGroup}</div>
                </div>
              )}
              {staff.nidOrPassport && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">NID/Passport</div>
                  <div className="text-sm font-medium text-gray-900">{staff.nidOrPassport}</div>
                </div>
              )}
            </div>
          </div>

          {/* Professional Information */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Professional Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Role</div>
                <div className="text-sm font-medium text-gray-900">{getRoleDisplayName(staff.role)}</div>
              </div>
              {staff.department && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Department</div>
                  <div className="text-sm font-medium text-gray-900">{staff.department}</div>
                </div>
              )}
              {staff.qualification && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Qualification</div>
                  <div className="text-sm font-medium text-gray-900">{staff.qualification}</div>
                </div>
              )}
              {staff.licenseNo && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">License Number</div>
                  <div className="text-sm font-medium text-gray-900">{staff.licenseNo}</div>
                </div>
              )}
              {staff.specialization && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Specialization</div>
                  <div className="text-sm font-medium text-gray-900">{staff.specialization}</div>
                </div>
              )}
              {staff.experienceYears !== undefined && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Experience</div>
                  <div className="text-sm font-medium text-gray-900">{staff.experienceYears} years</div>
                </div>
              )}
            </div>
          </div>

          {/* Payroll Information */}
          {(staff.salary || staff.bankAccount || staff.taxId) && (
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Payroll Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {staff.salary && (
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Salary</div>
                    <div className="text-sm font-medium text-gray-900">${staff.salary.toLocaleString()}</div>
                  </div>
                )}
                {staff.bankAccount && (
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Bank Account</div>
                    <div className="text-sm font-medium text-gray-900">{staff.bankAccount}</div>
                  </div>
                )}
                {staff.taxId && (
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Tax ID</div>
                    <div className="text-sm font-medium text-gray-900">{staff.taxId}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* ID Card Status */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-medium text-gray-900 mb-4">ID Card Status</h3>
            {staff.cardIssuedAt ? (
              <div className="text-center">
                <div className="text-green-600 font-medium">Card Issued</div>
                <div className="text-sm text-gray-500 mt-1">
                  {new Date(staff.cardIssuedAt).toLocaleDateString()}
                </div>
                <button
                  onClick={handleGenerateIdCard}
                  disabled={generatingCard}
                  className="mt-3 w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
                >
                  {generatingCard ? 'Generating...' : 'Regenerate Card'}
                </button>
              </div>
            ) : (
              <div className="text-center">
                <div className="text-gray-500 font-medium">No Card Issued</div>
                <button
                  onClick={handleGenerateIdCard}
                  disabled={generatingCard}
                  className="mt-3 w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
                >
                  {generatingCard ? 'Generating...' : 'Generate ID Card'}
                </button>
              </div>
            )}
          </div>

          {/* Account Status */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Account Status</h3>
            <AccountSummary staffId={staff.id} />
          </div>

          {/* System Information */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-medium text-gray-900 mb-4">System Information</h3>
            <div className="space-y-3">
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Created</div>
                <div className="text-sm font-medium text-gray-900">
                  {new Date(staff.createdAt).toLocaleDateString()}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Last Updated</div>
                <div className="text-sm font-medium text-gray-900">
                  {new Date(staff.updatedAt).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffProfilePage;
